import React, { createContext, useContext, useEffect, useState } from "react";

const API = process.env.REACT_APP_BACKEND_URL;
const TOKEN_KEY = "cx_token";

export function formatError(detail, fallback = "Something went wrong") {
  if (detail == null) return fallback;
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => (e && e.msg) || JSON.stringify(e)).join(" ");
  if (detail.message) return detail.message + (detail.errors ? ": " + detail.errors.join("; ") : "");
  if (detail.msg) return detail.msg;
  return JSON.stringify(detail);
}

export async function api(path, { method = "GET", body, headers = {}, raw = false, tenant } = {}) {
  const token = localStorage.getItem(TOKEN_KEY);
  const h = { ...headers };
  if (body !== undefined && !(body instanceof FormData)) h["Content-Type"] = "application/json";
  if (token) h["Authorization"] = `Bearer ${token}`;
  const t = tenant || localStorage.getItem("cx_tenant");
  if (t) h["X-CoreX-Tenant"] = t;
  const res = await fetch(`${API}/api${path}`, { method, headers: h, credentials: "include", body: body !== undefined ? JSON.stringify(body) : undefined });
  if (raw) return res;
  if (res.status === 204) return null;
  const text = await res.text();
  let data = null;
  try { data = text ? JSON.parse(text) : null; } catch { data = { detail: text }; }
  if (!res.ok) {
    const err = new Error(formatError(data && data.detail, `HTTP ${res.status}`));
    err.status = res.status; err.detail = data && data.detail;
    throw err;
  }
  return data;
}

const AuthCtx = createContext(null);

export function AuthProvider({ children }) {
  const [state, setState] = useState({ loading: true, user: null, tenant: null });
  const refresh = async () => {
    try { const me = await api("/auth/me"); setState({ loading: false, user: me.user, tenant: me.tenant }); }
    catch { setState({ loading: false, user: null, tenant: null }); }
  };
  useEffect(() => { refresh(); }, []);
  const login = async (email, password) => {
    const r = await api("/auth/login", { method: "POST", body: { email, password } });
    localStorage.setItem(TOKEN_KEY, r.token);
    setState({ loading: false, user: r.user, tenant: r.tenant });
    return r;
  };
  const logout = async () => {
    try { await api("/auth/logout", { method: "POST" }); } catch { /* ignore */ }
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem("cx_tenant");
    setState({ loading: false, user: null, tenant: null });
  };
  return <AuthCtx.Provider value={{ ...state, login, logout, refresh }}>{children}</AuthCtx.Provider>;
}

export const useAuth = () => useContext(AuthCtx);
